import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ImportRun, importacoesService } from '@/services/importacoes-service'
import { apiErrorMessage } from '@/services/team-service'
import * as toast from '@/lib/toast'
import { formatDateTime, RUN_STATUS_LABELS, runPeriodLabel, runStatusVariant } from './run-labels'

interface ImportRunsTableProps {
  runs: ImportRun[]
}

/** Histórico das importações; a linha expande com o resultado por monitoramento. */
export function ImportRunsTable({ runs }: ImportRunsTableProps) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const [details, setDetails] = useState<Record<string, ImportRun>>({})

  const toggle = async (run: ImportRun) => {
    if (expanded === run.id) return setExpanded(null)
    setExpanded(run.id)
    try {
      const detail = await importacoesService.getRun(run.id)
      setDetails((current) => ({ ...current, [run.id]: detail }))
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Não foi possível carregar a execução'))
    }
  }

  if (runs.length === 0) return <p className="text-sm text-muted-foreground">Nenhuma importação registrada ainda.</p>

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Início</TableHead>
          <TableHead>Origem</TableHead>
          <TableHead>Período</TableHead>
          <TableHead>Situação</TableHead>
          <TableHead className="text-right">Novas</TableHead>
          <TableHead className="text-right">Repetidas</TableHead>
          <TableHead className="text-right">Erros</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <Fragment key={run.id}>
            <TableRow className="cursor-pointer" onClick={() => toggle(run)}>
              <TableCell>
                <button type="button" aria-label={expanded === run.id ? 'Recolher' : 'Ver monitoramentos'}>
                  {expanded === run.id ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                </button>
              </TableCell>
              <TableCell className="tabular-nums">{formatDateTime(run.iniciadoEm)}</TableCell>
              <TableCell>
                {run.origem === 'AGENDADA' ? 'Agendada' : `Manual${run.iniciadoPorNome ? ` · ${run.iniciadoPorNome}` : ''}`}
                {run.escopo === 'MONITORAMENTO' && <span className="text-muted-foreground"> (um monitoramento)</span>}
              </TableCell>
              <TableCell>{runPeriodLabel(run)}</TableCell>
              <TableCell><Badge variant={runStatusVariant(run.status)}>{RUN_STATUS_LABELS[run.status]}</Badge></TableCell>
              <TableCell className="text-right tabular-nums">{run.novas}</TableCell>
              <TableCell className="text-right tabular-nums">{run.repetidas}</TableCell>
              <TableCell className="text-right tabular-nums">{run.erros}</TableCell>
            </TableRow>
            {expanded === run.id && (
              <TableRow>
                <TableCell />
                <TableCell colSpan={7}>
                  {!details[run.id] ? (
                    <span className="text-sm text-muted-foreground">Carregando…</span>
                  ) : (
                    <ul className="flex flex-col gap-1 text-sm">
                      {(details[run.id].itens ?? []).map((item) => (
                        <li key={item.id} className="flex flex-wrap gap-2">
                          <span className="font-medium">{item.alvo}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {item.novas} novas · {item.repetidas} repetidas · {item.tentativas} tentativa(s)
                          </span>
                          {item.erro && <span className="text-destructive">{item.erro}</span>}
                        </li>
                      ))}
                      {(details[run.id].itens ?? []).length === 0 && <li className="text-muted-foreground">Nenhum monitoramento ativo nesta execução.</li>}
                    </ul>
                  )}
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  )
}
